export const SETTINGS_SECTIONS = [
  "general",
  "appearance",
  "features",
  "integrations",
  "team",
  "codesmith",
] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

export const CODESMITH_SETTINGS_SUBSECTIONS = [
  "general",
  "mcp",
  "skills",
  "memory",
  "environment",
  "review",
] as const;
export type CodesmithSettingsSubsection =
  (typeof CODESMITH_SETTINGS_SUBSECTIONS)[number];

export const SETTINGS_SECTION_TITLES: Record<SettingsSection, string> = {
  general: "General",
  appearance: "Appearance",
  features: "Features",
  integrations: "Integrations",
  team: "Team",
  codesmith: "[code]smith",
};

export type SettingsRoute = {
  /** Org segment as written in the pathname; `[org]` on route patterns. */
  org: string | null;
  section: SettingsSection;
  subsection: CodesmithSettingsSubsection | null;
};

function isSection(value: string | null): value is SettingsSection {
  return (SETTINGS_SECTIONS as readonly string[]).includes(value ?? "");
}

function isSubsection(
  value: string | null,
): value is CodesmithSettingsSubsection {
  return (CODESMITH_SETTINGS_SUBSECTIONS as readonly string[]).includes(
    value ?? "",
  );
}

/**
 * `settingsPath("acme", "codesmith", "mcp")` → `/acme/settings/codesmith/mcp`;
 * `settingsPath(null)` → `/settings` (OrgRedirect fills in the active org).
 */
export function settingsPath(
  org: string | null | undefined,
  section: SettingsSection = "general",
  subsection: CodesmithSettingsSubsection | null = null,
): string {
  const base = org ? `/${org}/settings` : "/settings";
  if (section === "general") return base;
  if (section === "codesmith" && subsection && subsection !== "general") {
    return `${base}/codesmith/${subsection}`;
  }
  return `${base}/${section}`;
}

const SETTINGS_PATHNAME =
  /^\/(?:([^/]+)\/)?settings(?:\/([^/]+))?(?:\/([^/]+))?\/?$/;

/**
 * Accepts concrete pathnames and route patterns alike:
 * `/acme/settings/codesmith/mcp` and `/[org]/settings/codesmith/mcp` both
 * give `{ org, section: "codesmith", subsection: "mcp" }`. Unknown sections
 * are not settings routes. The org-less pattern `/settings/[[...section]]`
 * (a redirect into the active org) reads as the general section.
 */
export function settingsRouteFromPathname(
  pathname: string,
): SettingsRoute | null {
  const match = SETTINGS_PATHNAME.exec(pathname);
  if (!match) return null;
  const [, org = null, first = null, second = null] = match;
  if (first === null || (org === null && first === "[[...section]]")) {
    return { org, section: "general", subsection: null };
  }
  if (!isSection(first) || first === "general") return null;
  if (second === null) {
    return {
      org,
      section: first,
      subsection: first === "codesmith" ? "general" : null,
    };
  }
  if (first !== "codesmith" || !isSubsection(second) || second === "general") {
    return null;
  }
  return { org, section: "codesmith", subsection: second };
}

const MCP_DEEP_LINK_PARAMS = ["mcp_action", "mcp_knowledge"];

/**
 * Where a settings URL that predates the section routes should land, or null
 * when it already points at its section. Such URLs select the section with
 * `?tab=`/`?subtab=` and imply the MCP sub-section through deep-link params
 * (`mcp_action`, `mcp_knowledge`, `#mcp-servers`); they still live in old
 * Slack messages and GitHub comments.
 *
 * `/acme/settings?tab=codesmith&subtab=mcp&mcp_action=add#mcp-servers`
 *   → `/acme/settings/codesmith/mcp?mcp_action=add#mcp-servers`
 * `/settings?tab=integrations&connect=slack` → `/settings/integrations?connect=slack`
 */
export function legacySettingsRedirect(url: {
  pathname: string;
  search: string;
  hash: string;
}): string | null {
  const route = settingsRouteFromPathname(url.pathname);
  if (!route) return null;

  const params = new URLSearchParams(url.search);
  const tab = params.get("tab");
  const subtab = params.get("subtab");
  const hasLegacyParams = tab !== null || subtab !== null;
  params.delete("tab");
  params.delete("subtab");

  let { section, subsection } = route;
  if (section === "general" && isSection(tab)) section = tab;
  const onCodesmithGeneral =
    section === "codesmith" && (subsection ?? "general") === "general";
  const impliesMcp =
    onCodesmithGeneral &&
    (MCP_DEEP_LINK_PARAMS.some((key) => params.has(key)) ||
      url.hash === "#mcp-servers");
  if (!hasLegacyParams && !impliesMcp) return null;
  if (onCodesmithGeneral) {
    if (isSubsection(subtab)) subsection = subtab;
    else if (impliesMcp) subsection = "mcp";
  }

  const search = params.toString();
  return `${settingsPath(route.org, section, subsection)}${
    search ? `?${search}` : ""
  }${url.hash}`;
}
